import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { CopyPlus } from 'lucide-react';
import { CARD_TITLE_MAX_LENGTH } from '@molt/shared';
import { Button, ListPanel, TextField } from '@/components';
import { announce } from '@/lib/announce';
import { cn } from '@/lib/cn';
import { useCreateCard } from '@/lib/cards';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * The card composer — `FE-12.1`, `FR-BOARD-020`. `C3-BRIEF.md` §6.3 fixes the
 * props; `ListComposer.tsx` (chunk `C2`) is the template this file follows
 * for every hard part, named below where this file diverges from it.
 *
 * ── AC1 is the item's whole point ─────────────────────────────────────────
 * On submit the composer stays OPEN AND EMPTY with focus retained, in the
 * same tick as the optimistic mutation — not after a response. `handleSubmit`
 * clears `title` and refocuses the field itself; it does not wait on
 * `createCard`'s promise. A composer that closes on submit would pass every
 * other criterion in `FR-BOARD-020` and fail the one it was written for.
 *
 * ── Fully controlled `open` ────────────────────────────────────────────────
 * The board owns which one composer is open anywhere (`BoardFace`, agent G) —
 * this component never decides to open or close itself outside Escape/Cancel,
 * both of which call `onOpenChange(false)` rather than hiding locally.
 *
 * ── Focus is imperative, not `autoFocus` ──────────────────────────────────
 * The same `inputRef.current?.focus()` call fires on open (via the
 * effect+rAF pair below) and again after every submit while the composer
 * stays mounted (inline in `handleSubmit`, since the effect's `[open]`
 * dependency does not re-run while `open` stays `true` across submits).
 *
 * ── The client never computes a rank (brief §3.1) ─────────────────────────
 * `previousCardId`/`nextCardId` pass straight through to `useCreateCard` —
 * this file synthesises no `position` and imports nothing from any rank
 * module. `placement` only decides which gap the CALLER hands in; it plays
 * no part in the request itself.
 *
 * ⚠ **A consequence for the CALLER, not fixable in this file**: AC1 exists so
 * successive cards can be typed without re-opening the composer, which means
 * `previousCardId`/`nextCardId` must stay valid across back-to-back submits
 * while the first create is still in flight. Between submit #1 and submit
 * #2, the caller re-renders off the optimistically-updated cache, and the
 * neighbour this composer sits against may now BE the placeholder submit #1
 * just inserted — an `optimistic:` id, which is a 404 on the wire (brief
 * §3.3, "a neighbour that is not a live card"). This component has no list
 * to walk and cannot gate on `isOptimisticCardId` itself (§6.3 fixes the
 * pair as pass-through); it is the fifth site §5.4 asks every agent to check
 * itself against, and it is the caller's to fix. See this file's return to
 * the parent for the two concrete fixes (one per `placement`).
 *
 * ── The field is multiline, and Enter is special-cased ────────────────────
 * A card title is a sentence, not a word, so the field is `multiline`. A
 * `<textarea>` never submits its form on Enter by default (that is
 * single-line-input-only browser behaviour), so this file intercepts Enter
 * itself: a typed Enter (no Shift) submits; Shift+Enter inserts a newline by
 * doing nothing and letting the browser's default happen. A **pasted**
 * multi-line string never passes through a keydown handler at all — paste
 * lands as a `change` — so it stays one card with the newlines in the title.
 * Splitting a paste into several cards is `FR-BOARD-021`, which is V1 and has
 * no item in this chunk.
 *
 * ── AC4 — keeping the composer in sight ────────────────────────────────────
 * This is C2's composer-clipping defect in the vertical axis: C2 fixed the
 * horizontal one with `scroll-px-3` on the board strip, and the vertical
 * equivalent is `scroll-py-*` on the card `<ol>` that scrolls (`ListColumn`,
 * agent F's file — not this one). What THIS file does is stay out of the way
 * of that fix: it never calls `scrollIntoView` on anything. Focusing the
 * field imperatively already asks the browser to scroll the nearest
 * scrollable ancestor to reveal it, and a scroll-padded ancestor is what
 * makes that native behaviour land the composer fully in view rather than
 * flush against the edge. Calling `scrollIntoView` on top of that is exactly
 * what caused C2's second clipping bug — it scrolls to the ELEMENT's
 * bounding box, ignoring the padded safe area, and can re-clip a composer a
 * padded ancestor had already positioned correctly.
 *
 * ── FE-80.1: five strings, and the one that was already declared ───────────
 * This file had FIVE English strings and ZERO `t(` call sites while being the
 * control every card on every board is created through. Four became
 * `boardFace.card.composer.*`, mirroring the `boardFace.list.composer.*` block
 * `ListComposer.tsx` earned one slice earlier.
 *
 * ⚠ The fifth was NOT a new key. `` announce(`Card ${trimmedTitle} added.`) ``
 * is `boardFace.card.added`, which the catalogue has declared since the
 * board-face slice and which `BoardFace.tsx:3456` already routes for the
 * paste-a-single-card path. Declaring a sibling for it would have made this
 * file a second author of a sentence that is already spoken, in the same live
 * region, about the same subject — the defect the undo slice spent a mutation
 * proving. The key is REUSED, not re-declared.
 *
 * ⚠ And it is passed `trimmedTitle` RAW where `BoardFace.tsx`'s call site
 * wraps its title in `cardDisplayTitle(t, …)`. That is not an omission: the
 * helper is an empty-title fallback and nothing else (`lib/card-title.ts`),
 * and `canSubmit` above refuses a whitespace-only title before the mutation is
 * ever called — so the fallback arm is unreachable from here, and reaching for
 * it would import a card-back-family key (`board.card.untitled`) into a branch
 * that cannot run.
 */
export interface CardComposerProps {
  boardId: string;
  listId: string;
  listTitle: string;
  /** 'bottom' appends — `FR-BOARD-020` AC1. 'top' prepends — AC2. */
  placement: 'bottom' | 'top';
  /** The card the new one goes below — null for the top composer and for an empty list. */
  previousCardId: string | null;
  /** The card it goes above — null for the bottom composer and for an empty list. */
  nextCardId: string | null;
  /** Controlled: the board owns which one composer is open, so only ever one is. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPasteMany?:
    | ((input: {
        text: string;
        previousCardId: string | null;
        nextCardId: string | null;
        listId: string;
        listTitle: string;
      }) => void)
    | undefined;
  canCreateFromTemplate?: boolean | undefined;
  templateLoading?: boolean | undefined;
  onCreateFromTemplate?:
    | ((input: {
        listId: string;
        listTitle: string;
        previousCardId: string | null;
        nextCardId: string | null;
      }) => void)
    | undefined;
  className?: string;
}

/**
 * `TextField`'s own rule: a counter earns its place only once it has
 * something to say. 512 is the wire limit (`CARD_TITLE_MAX_LENGTH`); a
 * counter that showed for every ordinary card title would shout on the
 * common case, so it stays hidden until 50 characters remain — the same
 * threshold `ListComposer` uses for the same reason.
 */
const TITLE_COUNTER_WARN_AT = CARD_TITLE_MAX_LENGTH - 50;

export function CardComposer({
  boardId,
  listId,
  listTitle,
  // `placement` is not read here — see "The client never computes a rank"
  // above. It exists so the CALLER (`ListColumn`, agent F) knows which gap
  // it is placing this composer into; this component's own behaviour is
  // identical for 'top' and 'bottom' once `previousCardId`/`nextCardId` are
  // in hand. Destructuring it out of `props` (rather than omitting it) keeps
  // it visible in this file as a prop this component's contract accepts.
  placement: _placement,
  previousCardId,
  nextCardId,
  open,
  onOpenChange,
  onPasteMany,
  canCreateFromTemplate = false,
  templateLoading = false,
  onCreateFromTemplate,
  className,
}: CardComposerProps): ReactElement {
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const createCard = useCreateCard(boardId);
  const { t } = useInterfaceLanguage();

  // Mirrors `ListComposer`: fires whenever this composer becomes the open
  // one, from whichever entry point set `open` to true (the bottom trigger,
  // the header trigger, `ListEmpty`'s button for an empty list — none of
  // which live in this file).
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();

    // Second call, next frame — the same belt-and-braces `ListComposer` needs
    // and for the same reason: an entry point that itself runs focus
    // restoration as it closes (a `Menu` item, a `Button` losing its own
    // focus) can still be mid-transition when this effect's synchronous call
    // runs, and wins the render but loses the frame. Cancelled on unmount so
    // a composer closed within the frame cannot steal focus on its way out.
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  if (!open) {
    // The board decides which composer (if any) is open; this file renders
    // nothing when it is not the one. An empty fragment, not `null`, so the
    // export keeps its fixed `ReactElement` return type for the other agents
    // compiling against it (brief §6.3).
    return <></>;
  }

  const trimmedTitle = title.trim();
  const canSubmit = trimmedTitle.length > 0;

  function closeWithoutCreating() {
    setTitle('');
    onOpenChange(false);
  }

  function submitCard() {
    // The edge case: whitespace-only is refused client-side, before the
    // request — the 422 is the backstop, not the mechanism (brief §3.3).
    if (!canSubmit) return;

    createCard.mutate({
      listId,
      title: trimmedTitle,
      previousCardId,
      nextCardId,
    });
    announce(t('boardFace.card.added', { title: trimmedTitle }));

    // AC1: stays open and empty, focus retained, in the same tick as the
    // (optimistic) mutation call — not after any response.
    setTitle('');
    inputRef.current?.focus();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    submitCard();
  }

  // AC3 (this item's Escape edge case): closes without creating. Attached to
  // the form rather than just the field so it also fires with focus on the
  // submit or cancel button — same as `ListComposer`.
  function handleFormKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    closeWithoutCreating();
  }

  // A typed Enter submits; Shift+Enter inserts a newline. This has to live on
  // the field itself (rather than relying on native form-submit-on-Enter,
  // which never applies to a `<textarea>`) precisely because the field is
  // multiline.
  function handleFieldKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    submitCard();
  }

  function handlePaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const text = event.clipboardData.getData('text');
    if (!text.includes('\n') && !text.includes('\r')) return;
    if (onPasteMany === undefined) return;
    event.preventDefault();
    onPasteMany({ text, previousCardId, nextCardId, listId, listTitle });
  }

  function handleCreateFromTemplate() {
    onCreateFromTemplate?.({ listId, listTitle, previousCardId, nextCardId });
  }

  const canOpenTemplateCreator =
    !templateLoading && canCreateFromTemplate && onCreateFromTemplate !== undefined;
  const showTemplateCreator = templateLoading || canOpenTemplateCreator;

  return (
    // No built-in margin, mirroring `ListComposer` exactly: the caller
    // places this component (inside or outside the card `<ol>`, before or
    // after the cards) and already owns the gap around it via its own
    // flex/`gap` layout or `className`. Guessing a margin here risks fighting
    // whatever `ListColumn` (agent F) already applies — precisely the kind
    // of between-files defect this brief exists to prevent.
    <ListPanel className={cn('w-full', className)}>
      <form
        onSubmit={handleSubmit}
        onKeyDown={handleFormKeyDown}
        className="flex flex-col gap-2"
      >
        <TextField
          ref={inputRef}
          label={t('boardFace.card.composer.name')}
          multiline
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={handleFieldKeyDown}
          onPaste={handlePaste}
          maxLength={CARD_TITLE_MAX_LENGTH}
          counter={{
            value: title.length,
            max: CARD_TITLE_MAX_LENGTH,
            warnAt: TITLE_COUNTER_WARN_AT,
          }}
        />
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!canSubmit}
              className="shrink-0"
            >
              {t('boardFace.card.composer.submit')}
            </Button>
            <Button
              type="button"
              variant="quiet"
              size="sm"
              onClick={closeWithoutCreating}
              className="shrink-0"
            >
              {t('boardFace.card.composer.cancel')}
            </Button>
          </div>
          {showTemplateCreator && (
            <Button
              type="button"
              variant="quiet"
              size="sm"
              iconStart={<CopyPlus aria-hidden="true" className="size-4" />}
              onClick={handleCreateFromTemplate}
              disabled={!canOpenTemplateCreator}
              className="w-full justify-start"
            >
              {t('boardFace.card.composer.fromTemplate')}
            </Button>
          )}
        </div>
      </form>
    </ListPanel>
  );
}
